import { LitElement, html } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { GoonAPIClient, Post, UserProfile, Comment } from '../client/ApiClient';
import { consume } from '@lit/context';
import { authContext, AuthState } from '../context/AuthContext';
import { API_ENDPOINT } from '../constants';
import { isLikelyUrl } from '../utils/url';
import AppAnalytics from '../client/AnalyticsClient';
import { MediaRemoteControl } from 'vidstack';

const client = new GoonAPIClient(API_ENDPOINT);

const MIN_VIEW_DURATION_MS = 1000;

@customElement('post-card')
export class PostCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) post: Post | null = null;
  @property({ type: Object }) creator: UserProfile | null = null;

  @state() showComments = false;
  @state() showReactions = false;
  @state() comments: Comment[] = [];
  @state() commentUsers: UserProfile[] = [];
  @state() commentText = '';
  @state() reactionCounts: Record<string, number> = {};
  @state() userReaction: string = '';

  @state() isFollowing = false;

  private loadIntersectionObserver?: IntersectionObserver;

  @state() private contentVisible = false;

  private playDebounceTimer: number | null = null;

  @state() viewStart: null | number = null;

  @consume({ context: authContext, subscribe: true })
  auth!: AuthState;

  async firstUpdated() {
    window.viewedPostIds = window.viewedPostIds || new Set<string>();

    if (this.auth.userId && this.creator) {
      const following = await client.getFollowingProfiles();
      this.isFollowing = following.following.some(p => p.id === this.creator.id);
    }
    if (this.post) {
      const reacts = await client.getPostReactions(this.post.id)
      this.reactionCounts = reacts.counts
      this.userReaction = reacts.userReaction
    }

    this.loadIntersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (this.viewStart === null) {
            this.viewStart = performance.now();
          }

          this.showContent();
          // Load next <post-card>
          const next = this.nextElementSibling as PostCard | null;
          if (next?.showContent) next.showContent();
        } else if (entry.intersectionRatio < 0.21) {

          if (this.viewStart !== null && this.post) {
            const viewDuration = performance.now() - this.viewStart;
            this.viewStart = null;

            if (viewDuration >= MIN_VIEW_DURATION_MS) {
              if (!window.viewedPostIds.has(this.post.id) && viewDuration >= MIN_VIEW_DURATION_MS) {
                window.viewedPostIds.add(this.post.id);
                AppAnalytics.getPlausible()?.trackEvent('post_view', {
                  props: {
                    postId: this.post.id,
                    userId: this.auth.userId || 'anonymous',
                    durationMs: Math.round(viewDuration / 1000).toFixed(0),
                  }
                });
              }
            }
          }

          this.hideContent();
        }
      }
    }, {
      threshold: 0.2,
      rootMargin: '100px 0px 100px 0px',
    });

    this.loadIntersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.loadIntersectionObserver?.disconnect();
    super.disconnectedCallback();
  }

  async checkAuthOrPrompt() {
    if (!this.auth.userId) {
      this.dispatchEvent(new CustomEvent('open-auth-modal', { bubbles: true, composed: true }));
      return false;
    }
    return true;
  }

  async handleReaction(emoji: string) {
    if (!this.post || !(await this.checkAuthOrPrompt())) return;

    const postId = this.post.id;

    if (this.userReaction === emoji) {
      // Unset
      await client.unsetPostReaction(postId);
      this.reactionCounts[emoji] = Math.max((this.reactionCounts[emoji] || 1) - 1, 0);
      this.userReaction = '';
    } else {
      // Update to new emoji
      if (this.userReaction) {
        // Decrement previous
        this.reactionCounts[this.userReaction] = Math.max((this.reactionCounts[this.userReaction] || 1) - 1, 0);
      }
      await client.setPostReaction(postId, emoji);
      this.reactionCounts[emoji] = (this.reactionCounts[emoji] || 0) + 1;
      this.userReaction = emoji;

      AppAnalytics.getPlausible()?.trackEvent('reaction', {
        props: {
          postId: this.post.id,
          userId: this.auth.userId || 'anonymous',
          emoji
        }
      });
    }

    this.showReactions = false;
    this.requestUpdate();
  }

  async handleFollow() {
    if (!(await this.checkAuthOrPrompt()) || !this.creator) return;
    if (this.isFollowing) {
      await client.unfollowUser(this.creator.id);
      this.isFollowing = false;
    } else {
      await client.followUser(this.creator.id);
      this.isFollowing = true;
    }
  }

  async handleComment() {
    if (!(await this.checkAuthOrPrompt()) || !this.post) return;
    const comment = await client.addComment(this.post.id, this.commentText);
    this.comments.unshift(comment);
    this.commentUsers.push(this.auth.user);
    this.commentText = '';
  }

  async toggleComments() {
    this.showComments = !this.showComments
    if (this.post) {
      const res = await client.getComments(this.post.id);
      this.comments = res.comments;
      this.commentUsers = res.users;
    }
  }

  async handleDeletePost() {
    if (!this.post) return;
    const confirmed = confirm("Are you sure you want to delete this post?");
    if (!confirmed) return;

    try {
      await client.deletePost(this.post.id);
      this.dispatchEvent(new CustomEvent('post-deleted', {
        detail: { postId: this.post.id },
        bubbles: true,
        composed: true,
      }));
    } catch (e) {
      alert("Failed to delete post.");
    }
  }

  async showContent() {
    this.contentVisible = true;

    // Clear any pending play attempts
    if (this.playDebounceTimer) {
      clearTimeout(this.playDebounceTimer);
    }

    this.playDebounceTimer = window.setTimeout(async () => {
      await this.updateComplete;

      if (this.post) {
        const remote = new MediaRemoteControl();
        const playerEl = document.getElementById(`media-player-${this.post.id}`);
        if (playerEl) {
          const video = playerEl.querySelector('video')
          if (video) {
            video.play()
            video.onloadeddata = () => {
              remote.setTarget(playerEl);
              remote.play();
            }
          }
        }
      }
    }, 150);
  }

  hideContent() {
    this.contentVisible = false;
  }

  render() {
    const reactionList = ['❤️', '💦', '🍆', '🍑', '🍒', '🤣', '👀', '😍', '😵'];

    return this.post && this.creator ? html`
      <div class="card bg-base-300 shadow-xl p-6 rounded-2xl space-y-4 min-h-[400px]">
        <div class="flex items-start justify-between">
          <div class="flex gap-3 items-center">
            <a href="/creator/${this.creator.slug}" class="flex gap-2 items-center">
              <img class="w-12 h-12 rounded-full border border-base-300 ring ring-primary ring-offset-base-100 ring-offset-2" src="${this.creator.avatar_url || '/static/images/default-avatar.webp'}" alt="${this.creator.display_name}" />
              <div>
                <div class="font-semibold text-base">${this.creator.display_name}</div>
                <div class="text-sm text-base-content/60">@${this.creator.slug}</div>
              </div>
            </a>
          </div>
          ${this.auth.userId === this.creator?.id ? html`
            <button class="btn btn-sm btn-outline btn-error plausible-event-name=postcard-delete-click"
                    @click=${this.handleDeletePost}>
              🗑️ Delete
            </button>
          ` : html`
            <button class="btn btn-sm btn-outline btn-primary plausible-event-name=postcard-${this.isFollowing ? 'unfollow' : 'follow'}-click"
                    @click=${this.handleFollow}>
              ${this.isFollowing ? 'Unfollow' : 'Follow'}
            </button>
          `}
        </div>

        <div class="">
          <h2 class="text-xl font-bold mb-2 text-primary">${this.post.title}</h2>
          ${this.contentVisible ? html`
            <div class="mt-3 h-[60dvh]">
            <post-media .mediaUrls=${this.post.media_urls} mediaPlayerId="media-player-${this.post.id}"></post-media>
              ${isLikelyUrl(this.post.body)
          ? html`<link-preview .url=${this.post.body?.trim()}></link-preview>`
          : html`<p class="whitespace-pre-wrap text-base-content/90">${this.post.body}</p>`}
            </div>
          ` : html`
            <div class="animate-pulse space-y-2 h-[60dvh]">
              <div class="bg-base-200 h-48 rounded-xl w-full"></div>
              <div class="bg-base-200 h-32 rounded-xl w-2/3"></div>
              <div class="h-8 bg-base-200 rounded w-1/2"></div>
              <div class="h-8 bg-base-200 rounded w-1/3"></div>
            </div>
          `}
        </div>

        <div class="flex justify-between items-center border-t pt-4">
          <div class="flex gap-2">
            <button class="btn btn-sm btn-outline btn-accent plausible-event-name=post-card-comment-click" @click=${() => this.toggleComments()}>💬 Comments...</button>
            <div class="relative">
              <button class="btn btn-sm btn-outline btn-error" @click=${() => this.showReactions = !this.showReactions}>♥️ React</button>
              ${this.showReactions ? html`
                <div class="absolute bottom-full mb-2 bg-base-300 rounded shadow p-2 flex gap-2 z-50 plausible-event-name=post-card-reaction-click">
                  ${reactionList.map(e => html`
                    <div class="badge text-base cursor-pointer border-accent! hover:border-primary! py-4 ${this.userReaction === e ? 'badge-primary' : 'badge-outline'}"
                      @click=${() => this.handleReaction(e)}
                    >
                      ${e}${this.reactionCounts[e] ? ` ${this.reactionCounts[e]}` : null}
                    </div>`)}
                </div>` : null}
            </div>
          </div>
          <div class="flex gap-2 hidden">
            <button class="btn btn-sm btn-outline btn-secondary plausible-event-name=post-card-add-to-autogooner-click" @click=${() => this.dispatchEvent(new CustomEvent('add-to-autogooner', { detail: { postId: this.post.id }, bubbles: true, composed: true }))}>🎬 Autogoon</button>
            <button class="btn btn-sm btn-outline btn-secondary plausible-event-name=post-card-mention-click" @click=${() => this.dispatchEvent(new CustomEvent('share-in-chat', { detail: { postId: this.post.id }, bubbles: true, composed: true }))}>📢 Mention</button>
          </div>
        </div>

        ${Object.keys(this.reactionCounts).length > 0 ? html`
          <div class="flex flex-wrap gap-2 mt-2">
            ${reactionList.map(e => this.reactionCounts[e] ? html`
              <button
                class="badge badge-outline text-base ${this.userReaction === e ? 'bg-primary text-primary-content' : ''}"
                @click=${() => this.handleReaction(e)}>
                ${e} ${this.reactionCounts[e]}
              </button>` : null)}
          </div>
        ` : null}

        ${this.showComments ? html`
          <div class="mt-2 border-t pt-4 space-y-2 max-h-48 overflow-y-auto">
            ${this.comments.length ? this.comments.map(comment => {
            const user = this.commentUsers.find(u => u.id === comment.user_profile_id);
            return html`
                <div class="flex items-start gap-3 text-sm">
                  <img src="${user?.avatar_url || '/static/images/default-avatar.webp'}" alt="${user?.display_name || 'User'}"
                       class="w-8 h-8 rounded-full border border-base-300" />
                  <div>
                    <div class="font-medium">${user?.display_name || 'Unknown User'}</div>
                    <div class="text-base-content/80 whitespace-pre-wrap">${comment.text}</div>
                  </div>
                </div>
              `;
          }) :
          html`<div class="text-sm text-base-content/70 italic">No comments yet</div>`}
            <div class="flex gap-2 mt-2">
              <input class="input input-sm w-full" placeholder="Add a comment..." .value=${this.commentText} @input=${e => this.commentText = e.target.value} />
              <button class="btn btn-sm btn-primary plausible-event-name=post-card-send-comment" @click=${this.handleComment}>Send</button>
            </div>
          </div>
        ` : null}
      </div>
    ` : null;
  }
}

